import axios from 'axios';
import { auth } from '../firebase';
import { BASEURL, getBusinessToken } from './nodeAxiosFirebase';
import { getDeviceHeaders } from './deviceInfo';
import { subscribeCoreSeqJobs, getCachedCoreSeqJob } from './coreSeqJobsBridge';

// Only DONE/ERROR are terminal — RETRYING means Cloud Tasks scheduled another
// attempt, QUEUED/PROCESSING mean the job hasn't run yet.
const TERMINAL_STATUSES = new Set(['done', 'error']);
const POLL_INTERVAL_MS = 1500;
// Polling is a fallback, not a second active channel: give the realtime push
// (near-instant on a healthy socket) a head start before spending any HTTP
// calls. Most jobs resolve well within this window, so the common case does
// zero polling requests.
const POLL_GRACE_PERIOD_MS = 3000;
const WAIT_TIMEOUT_MS = 40000;

async function pollJobOnce(jobId) {
  try {
    const businessToken = getBusinessToken();
    const user = auth.currentUser;
    const idToken = user
      ? await user.getIdToken()
      : null;
    const response = await axios({
      method: 'GET',
      url: `${BASEURL}coreSeqV6/job/${jobId}`,
      headers: {
        ...(idToken && { Authorization: `Bearer ${idToken}` }),
        ...(user && getDeviceHeaders(user.uid)),
        ...(businessToken && { businesstoken: businessToken }),
      },
      timeout: 10000,
    });
    return response.data;
  } catch (_) {
    // Polling is a fallback signal only — swallow transient failures, the
    // next tick (or the realtime push) will catch up.
    return null;
  }
}

function toResult(job) {
  if (job.status === 'done') {
    return { ok: true, data: job.result ?? null };
  }
  return {
    ok: false,
    errorData: {
      message: job.error?.message || job.error?.messages?.fr || 'coreSeqV6 job failed',
      status: job.error?.statusCode || 500,
      data: job.error || null,
      details: job.error?.details,
      code: job.error?.intCode,
    },
  };
}

/**
 * Waits for a coreSeqV6 job to reach a terminal state (done/error), combining
 * the realtime push (fast path, mirrored into coreSeqJobsBridge by
 * useRealtimeSocket.js) with a polling fallback against
 * `GET coreSeqV6/job/:jobId` — needed because the realtime channel only keeps
 * the 20 most recent jobs per business and the socket can be disconnected.
 *
 * Resolves to `{ ok: true, data }` or `{ ok: false, errorData }`; never
 * rejects, so callers (nodeAxiosFirebase.js) can preserve the same
 * `response.data` / `{ errorData }` contract callers already rely on.
 */
export function waitForCoreSeqJob(jobId) {
  return new Promise((resolve) => {
    let settled = false;
    let pollTimer = null;
    let pollGraceTimer = null;
    let timeoutTimer = null;
    let unsubscribe = null;
    let pollInFlight = false;

    const cleanup = () => {
      if (pollTimer) clearInterval(pollTimer);
      if (pollGraceTimer) clearTimeout(pollGraceTimer);
      if (timeoutTimer) clearTimeout(timeoutTimer);
      if (unsubscribe) unsubscribe();
    };

    const finish = (job) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(toResult(job));
    };

    const checkJob = (job) => {
      if (job && TERMINAL_STATUSES.has(job.status)) {
        finish(job);
      }
    };

    // Fast path: the job may already have finished before this function was
    // called (worker was idle, realtime push arrived first).
    checkJob(getCachedCoreSeqJob(jobId));
    if (settled) return;

    unsubscribe = subscribeCoreSeqJobs(() => {
      checkJob(getCachedCoreSeqJob(jobId));
    });

    const poll = async () => {
      if (settled || pollInFlight) return;
      pollInFlight = true;
      try {
        checkJob(await pollJobOnce(jobId));
      } finally {
        pollInFlight = false;
      }
    };

    pollGraceTimer = setTimeout(() => {
      if (settled) return;
      pollTimer = setInterval(poll, POLL_INTERVAL_MS);
      // Check as soon as the grace ends; slow requests never overlap.
      poll();
    }, POLL_GRACE_PERIOD_MS);

    timeoutTimer = setTimeout(() => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({
        ok: false,
        errorData: {
          message: 'Le traitement de la requête prend plus de temps que prévu.',
          status: 504,
          timeout: true,
        },
      });
    }, WAIT_TIMEOUT_MS);
  });
}
